import { useState } from "react";
import { Send, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Reveal from "./Reveal";

const EVENT_TYPES = [
  "Fête locale / votive",
  "Festival municipal",
  "Concert estival",
  "Fête nationale (14 juillet)",
  "Fête de la musique",
  "Autre événement",
];

const INITIAL = {
  collectivite: "",
  contact_name: "",
  email: "",
  phone: "",
  event_type: EVENT_TYPES[0],
  event_date: "",
  budget: "",
  message: "",
};

const inputCls =
  "w-full bg-[#09090D] border border-zinc-800 focus:border-amber-500/60 focus:outline-none rounded-xl px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300";

const BookingForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/booking-requests", form);
      toast.success("Demande envoyée !", {
        description: data.ack_sent
          ? "Un accusé de réception vient d'être envoyé à votre adresse email. Forty One vous recontacte vite."
          : "Votre demande est enregistrée. Forty One vous recontacte vite.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-form-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 80% 10%, rgba(157,78,221,0.16), transparent 65%), radial-gradient(ellipse 40% 40% at 10% 90%, rgba(216,0,166,0.10), transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-jbmono font-semibold text-amber-400 mb-4">
            Booking collectivités
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight uppercase leading-[0.95] mb-6">
            Forty One <span className="text-amber-500">sur votre</span> scène
            <span className="text-red-500">.</span>
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md mb-8">
            Mairie, comité des fêtes, office de tourisme : décrivez votre
            événement en deux minutes. Le groupe vous répond directement avec
            une proposition et un devis.
          </p>
          <ul className="space-y-3">
            {[
              "Réponse rapide et devis clair",
              "Répertoire adapté à votre public",
              "Plan de scène et fiche technique fournis",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                <PartyPopper size={15} className="text-amber-500 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="booking-form"
            onSubmit={submit}
            className="rounded-2xl border border-amber-500/20 bg-[#161622]/90 backdrop-blur-md p-6 sm:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Collectivité / organisme *
                </label>
                <input
                  data-testid="booking-collectivite-input"
                  required
                  value={form.collectivite}
                  onChange={set("collectivite")}
                  placeholder="Mairie de…"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Votre nom *
                </label>
                <input
                  data-testid="booking-contact-input"
                  required
                  value={form.contact_name}
                  onChange={set("contact_name")}
                  placeholder="Prénom Nom"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Email *
                </label>
                <input
                  data-testid="booking-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="contact@mairie.fr"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Téléphone
                </label>
                <input
                  data-testid="booking-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="04 00 00 00 00"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Type d'événement *
                </label>
                <select
                  data-testid="booking-event-type-select"
                  value={form.event_type}
                  onChange={set("event_type")}
                  className={inputCls}
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                  Date envisagée
                </label>
                <input
                  data-testid="booking-date-input"
                  type="date"
                  value={form.event_date}
                  onChange={set("event_date")}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                Budget indicatif
              </label>
              <input
                data-testid="booking-budget-input"
                value={form.budget}
                onChange={set("budget")}
                placeholder="Ex. : 1 500 €"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs font-jbmono uppercase tracking-widest text-zinc-400 mb-2">
                Votre projet
              </label>
              <textarea
                data-testid="booking-message-input"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Lieu, public attendu, scène disponible, horaires…"
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm rounded-xl px-6 py-4 transition-colors duration-300"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Envoi en cours…" : "Envoyer la demande"}
            </button>
            <p className="text-[11px] text-zinc-500 text-center">
              Vos informations ne servent qu'à traiter votre demande de concert.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default BookingForm;
